<template>
    <v-app-bar :elevation="2">
        <v-img
            class="mx-2 cursor-pointer"
            :src="logo"
            max-height="60"
            max-width="120"
            contain
            @click="goToHome"
        />

        <v-divider vertical />

        <v-tabs color="primary">
            <v-tab
                v-for="route in pageList"
                v-show="isRouteAllowed(route.meta.rights)"
                :key="route.path"
                :to="route.path"
            >
                {{ route.meta.display || `NO-DISPLAY: ${route.name}` }}
            </v-tab>
        </v-tabs>

        <v-spacer />

        <v-select
            :model-value="corporation"
            :disabled="corporations.length<=1"
            :item-title="item=> `${item.name} (${item.corporationNumber})`"
            item-value="uuid"
            return-object
            :items="corporations"
            max-width="500"
            @update:model-value="refreshRoute"
        />

        <v-menu location="bottom" open-on-click>
            <template #activator="{ props }">
                <v-btn
                    v-bind="props"
                    class="mx-4"
                    prepend-icon="mdi-account"
                >
                    {{ userName }}
                </v-btn>
            </template>

            <v-list>
                <v-list-item v-if="hasUserOtp===false" @click="openUpdatePasswordDialog">
                    Passwort ändern
                </v-list-item>
                <v-list-item v-if="hasUserOtp" @click="openResetPasswordDialog">
                    Passwort zurücksetzen
                </v-list-item>
                <v-list-item base-color="error" @click="logout">
                    Abmelden
                </v-list-item>
            </v-list>
        </v-menu>

        <PopUp
            ref="updatePasswordDialog"
            title="Passwort ändern"
            submit-label="Ändern"
            submit-prepend-icon="mdi-key-variant"
            @close="submit => closeUpdatePasswordDialog(submit)"
        >
            <template #content>
                <v-text-field
                    v-model="updatePassword.oldPassword"
                    label="Aktuelles Passwort"
                    required
                    type="password"
                    :rules="[
                        value => !!value || 'Pflichtfeld',
                    ]"
                />

                <v-text-field
                    v-model="updatePassword.newPassword"
                    label="Neues Passwort"
                    required
                    :rules="[
                        value => !!value || 'Pflichtfeld',
                        value => value !== updatePassword.oldPassword || 'Ihr neues Passwort darf nicht mit dem alten übereinstimmen!',
                    ]"
                    type="password"
                />

                <v-text-field
                    v-model="updatePassword.confirmPassword"
                    label="Neues Passwort bestätigen"
                    required
                    type="password"
                    :rules="[
                        value => !!value || 'Pflichtfeld',
                        value => value === updatePassword.newPassword || 'Die Passwörter müssen übereinstimmen',
                    ]"
                />
            </template>
        </PopUp>
        <PopUp
            ref="resetPasswordDialog"
            title="Passwort zurücksetzen"
            submit-label="Zurücksetzen"
            submit-prepend-icon="mdi-key-variant"
            @close="submit => closeResetPasswordDialog(submit)"
        >
            <template #content>
                Nach dem Klicken auf "Zurücksetzen" werden Sie ausgeloggt und können beim nächsten Login ein neues Passwort vergeben.
            </template>
        </PopUp>
    </v-app-bar>
</template>

<script setup>
import INJECTION_KEYS from '@injectionKeys'
import { computed, inject, onMounted, reactive, ref, useTemplateRef, watch } from 'vue'
import { useRouter } from 'vue-router'

import { ROUTES } from '@/router'
import { useKeycloak } from '@lib/keycloak.js'
import { MiddlewareConnector } from '@middleware-connector'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import logo from '/logo.svg'
import { useCurrentUser } from '@/composables/useCurrentUser'
const { errorMessage: currentUserErrorMessage, userName, logout, setUpdatePasswordAction, hasOtp } = useCurrentUser()

const keycloak = useKeycloak()

/** @type {Session} */
const session = inject(INJECTION_KEYS.SESSIONSTATE)
/** @type {SnackState} */
const snackState = inject(INJECTION_KEYS.SNACKSTATE)

const router = useRouter()

const pageOrder = [ ROUTES.HOME, ROUTES.APPLICATION_REQUESTS, ROUTES.ACCOUNTING, ROUTES.USERS ] //in which order the routes appear in the nav bar
const hasUserOtp = ref(false)

const corporations = ref([])
const updatePasswordDialog = useTemplateRef('updatePasswordDialog')
const resetPasswordDialog = useTemplateRef('resetPasswordDialog')

const updatePassword = reactive({
    show: false,
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
})
const updatePasswordRules = {
    oldPassword: {
        required
    },
    newPassword: {
        required,
        notTheSameAs: (value) => value !== updatePassword.oldPassword,
    },
    confirmPassword: {
        required,
        sameAs: (value) => value === updatePassword.newPassword
    }
}
const updatePasswordValidation = useVuelidate(updatePasswordRules, updatePassword)

const init = async () => {
    // async calls, we do not need them to wait for each other
    getCorporations()
    hasUserOtp.value = await hasOtp()
}

const pageList = computed(() => {

    let result = []
    const routes = router.getRoutes()

    pageOrder.forEach(i => {
        let routeMatch = routes.find(route => route.name === i)
        if (routeMatch) {
            result.push(routeMatch)
        }
        else {
            console.error('Could not find route', i)
        }
    })

    return result
})

const corporation = computed(() => {
    return corporations.value.find(c => c.uuid === session.corporation.uuid)
})

const openResetPasswordDialog = () => {
    resetPasswordDialog.value.openDialog()
}

const closeResetPasswordDialog = async (submit) => {
    if (submit) {
        await setUpdatePasswordAction()
        if (!currentUserErrorMessage.value) {
            logout()
        }
    }
    resetPasswordDialog.value.closeDialog()
}

const isRouteAllowed = (listOfRights) => {
    if (!listOfRights) return true
    return listOfRights.find((right) => keycloak.hasResourceRole(right))
}

const getCorporations = async () => {
    corporations.value = await MiddlewareConnector.getCompanies()
    .catch(err => {
        snackState.showErrorSnack(err.response?.data?.userMessage || err.message)
    })
}

const setCorporationInSession = (selectedCorporation) => {
    session.corporation = {
        uuid: selectedCorporation.uuid,
        name: selectedCorporation.name,
        number: selectedCorporation.corporationNumber,
    }
}

const goToHome = () => {
    session.uuidTicketholder = undefined
    router.push('/')
}

const refreshRoute = (selectedCorporation) => {
    if (session.uuidTicketholder) {
        const answer = window.confirm('Wollen Sie die Seite wirklich verlassen?')
        if (answer) {
            setCorporationInSession(selectedCorporation)
            session.uuidTicketholder = undefined
            goToHome()
        }
    } else {
        setCorporationInSession(selectedCorporation)
        router.go()
    }
}

const openUpdatePasswordDialog = () => {
    updatePasswordValidation.value.$reset()

    updatePassword.oldPassword = ''
    updatePassword.newPassword = ''
    updatePassword.confirmPassword = ''

    updatePasswordDialog.value.openDialog()
}

const closeUpdatePasswordDialog = async (submit) => {

    try {
        if (submit) {
            updatePasswordValidation.value.$reset()
            updatePasswordValidation.value.$touch()

            if (updatePasswordValidation.value.$error) {
                return
            }

            await MiddlewareConnector.handlePasswordChange(updatePassword.oldPassword, updatePassword.newPassword)

            snackState.showInfoSnack('Das Passwort wurde erfolgreich geändert.')
        }

        updatePasswordDialog.value.closeDialog()
    } catch (error) {
        if ([ 400, 405 ].includes(error.status)) {
            snackState.showErrorSnack('Das Passwort konnte nicht geändert werden.')
        } else {
            snackState.showErrorSnack(error.response?.data?.userMessage || error.message)
        }
    }
}

onMounted(() => {
    init()
})

watch(currentUserErrorMessage, (errorMessage) => {
    snackState.showErrorSnack(errorMessage)
})
</script>